/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBusinessProfile } from "../../context/BusinessProfileContext";
import { subscriptionService } from "../../services/SubscriptionService";
import { CheckIcon, XIcon, CheckCircleIcon } from "@heroicons/react/solid";
import {
  LockClosedIcon,
  CreditCardIcon,
  ArrowLeftIcon,
  MailIcon,
} from "@heroicons/react/outline";
import pebbleLogo from "../../assets/images/pebble-logo.png";
import LegalModal from "../../components/ui/LegalModal";
import { useTranslation } from "react-i18next";
import {
  signUpForProPlanNotify,
  checkProPlanNotifyStatus,
} from "../../firebaseFunctions/proPlanNotify/proPlanNotify";
import DOMAIN_CONFIG from "../../config/domains";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebaseFunctions/config";

const EOPaywall = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    user,
    userType,
    hasActiveSubscription,
    subscriptionTier,
    signOut,
    initialAuthCheckDone,
  } = useAuth();

  // Only redirect Pro users away - basic/free users should stay to upgrade
  const isProUser = subscriptionTier === 'pro' || subscriptionTier === 'premium' || subscriptionTier === 'enterprise';
  const { businessProfileData } = useBusinessProfile();
  const [selectedPlan, setSelectedPlan] = useState("basic");
  const [billingCycle, setBillingCycle] = useState("yearly");
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [showCanceled, setShowCanceled] = useState(false);
  const [legalModal, setLegalModal] = useState({ isOpen: false, type: null });
  const [isReady, setIsReady] = useState(false);

  const [showNotifyForm, setShowNotifyForm] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyStatus, setNotifyStatus] = useState("idle");
  const [notifyError, setNotifyError] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user?.email) {
      setNotifyEmail(user.email);
    }
  }, [user?.email]);

  useEffect(() => {
    const checkExistingSignup = async () => {
      if (user?.uid) {
        const status = await checkProPlanNotifyStatus(user.uid);
        if (status.success && status.alreadySignedUp) {
          setNotifyStatus("duplicate");
        }
      }
    };
    checkExistingSignup();
  }, [user?.uid]);

  const handleNotifySubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!notifyEmail || !notifyEmail.includes("@")) {
      setNotifyError("Please enter a valid email address");
      return;
    }

    setNotifyStatus("submitting");
    setNotifyError(null);

    try {
      const result = await signUpForProPlanNotify(
        user.uid,
        notifyEmail,
        billingCycle,
      );

      if (result.success) {
        if (result.alreadySignedUp) {
          setNotifyStatus("duplicate");
        } else {
          setNotifyStatus("success");
        }
      } else {
        setNotifyStatus("error");
        setNotifyError(result.error || "Failed to sign up. Please try again.");
      }
    } catch (err) {
      setNotifyStatus("error");
      setNotifyError("Something went wrong. Please try again.");
    }
  };

  const handleNotifyClick = (e) => {
    e.stopPropagation();
    if (notifyStatus === "duplicate" || notifyStatus === "success") {
      return;
    }
    setShowNotifyForm(true);
  };

  const plans = [
    {
      id: "basic",
      name: "Basic",
      monthlyPrice: 0,
      yearlyPrice: 0,
      description: "Start for free — Pebble takes 15% of vendor registration fees",
      features: [
        "Unlimited events",
        "Unlimited vendors per event",
        "Vendor management & applications",
        "Analytics dashboard",
        "Document uploads & signing",
        "Email support",
        "15% platform fee on vendor payments",
      ],
      popular: false,
      available: true,
      freemium: true,
    },
    {
      id: "pro",
      name: "Pro",
      monthlyPrice: 249,
      yearlyPrice: 2499,
      description: "More tools, more growth — for serious event organizers",
      features: [
        "Everything in Basic",
        "AI Event Builder — upload a flyer, fill your form instantly",
        "AI Vendor CRM — smart invites, tags & insights",
        "Attendee ticketing",
        "Document signing with PDF export",
        "Advanced analytics & reports",
        "3 seats (owner + 2 contributors)",
        "Priority support",
      ],
      popular: true,
      available: true,
    },
  ];

  useEffect(() => {
    if (searchParams.get("canceled") === "true") {
      setShowCanceled(true);
      setTimeout(() => setShowCanceled(false), 5000);
    }
  }, [searchParams]);

  // Direct Firestore check for hasActiveSubscription on mount
  // Only redirect Pro users away - basic/free users should stay to upgrade
  useEffect(() => {
    const checkFirestoreSubscription = async () => {
      if (!user?.uid || !initialAuthCheckDone) return;

      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const userTier = userData.subscriptionTier || userData.subscription?.plan || 'basic';
          const isPro = userTier === 'pro' || userTier === 'premium' || userTier === 'enterprise';

          // Only redirect if user is on Pro tier - basic/free users can stay to upgrade
          if ((userData.hasActiveSubscription === true || userData.hasActiveSubscription === 'true') && isPro) {
            console.log(
              "Firestore check: User has active Pro subscription, redirecting...",
            );

            // Check EO_BusinessProfiles collection for complete profile
            const profileDocRef = doc(db, "EO_BusinessProfiles", user.uid);
            const profileDocSnap = await getDoc(profileDocRef);

            if (profileDocSnap.exists()) {
              const profileData = profileDocSnap.data();
              const hasCompleteProfile =
                profileData.fullName?.trim() &&
                profileData.businessName?.trim();

              if (hasCompleteProfile) {
                DOMAIN_CONFIG.redirectToApp("/eo/dashboard");
              } else {
                navigate("/eo/complete-profile", { replace: true });
              }
            } else {
              // No profile record exists
              navigate("/eo/complete-profile", { replace: true });
            }
          }
        }
      } catch (error) {
        console.error("Error checking Firestore subscription status:", error);
      }
    };

    checkFirestoreSubscription();
  }, [user?.uid, initialAuthCheckDone, navigate]);

  useEffect(() => {
    // Wait for auth to fully load before making any redirect decisions
    if (!initialAuthCheckDone) return;

    const checkAndRedirect = async () => {
      // Only redirect Pro users away - basic/free users should stay to upgrade
      if (hasActiveSubscription && isProUser && userType === "EO" && user?.uid) {
        try {
          // Check EO_BusinessProfiles collection for complete profile
          const profileDocRef = doc(db, "EO_BusinessProfiles", user.uid);
          const profileDocSnap = await getDoc(profileDocRef);

          if (profileDocSnap.exists()) {
            const profileData = profileDocSnap.data();
            const hasCompleteProfile =
              profileData.fullName?.trim() && profileData.businessName?.trim();

            if (hasCompleteProfile) {
              DOMAIN_CONFIG.redirectToApp("/eo/dashboard");
            } else {
              navigate("/eo/complete-profile", { replace: true });
            }
          } else {
            // No profile record exists
            navigate("/eo/complete-profile", { replace: true });
          }
        } catch (error) {
          console.error("Error checking profile:", error);
          navigate("/eo/complete-profile", { replace: true });
        }
      }
    };

    checkAndRedirect();
  }, [hasActiveSubscription, isProUser, userType, navigate, user, initialAuthCheckDone]);

  useEffect(() => {
    // Wait for auth to fully load before making any redirect decisions
    if (!initialAuthCheckDone) return;

    if (!user) {
      navigate("/login", { replace: true });
      return;
    }

    // Only redirect if userType is explicitly set to something other than EO
    // If userType is null, user might be newly created - wait for it to be set
    if (userType && userType !== "EO") {
      navigate("/", { replace: true });
    }
  }, [user, userType, navigate, initialAuthCheckDone]);

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
  };

  const handleSubscribe = async (planId = null) => {
    const plan = planId || selectedPlan;
    if (isProcessing || !plan) return;

    setIsProcessing(true);
    setError(null);

    try {
      if (plan === "basic") {
        // Basic plan is free — activate directly and go to complete profile
        await subscriptionService.activateSubscription(user.uid, {
          status: "active",
          plan: "basic",
          currentPeriodEnd: null,
        });
        navigate("/eo/complete-profile", { replace: true });
        return;
      }
      // Redirect to Stripe Payment Link for paid plans
      subscriptionService.redirectToPaymentLink(user.uid, plan, billingCycle);
    } catch (err) {
      console.error("Checkout error:", err);
      setError(err.message || "Failed to start checkout. Please try again.");
      setIsProcessing(false);
    }
  };

  const getPrice = (plan) => {
    return billingCycle === "monthly" ? plan.monthlyPrice : plan.yearlyPrice;
  };

  const getSavings = (plan) => {
    const yearlyMonthly = plan.monthlyPrice * 12;
    const savings = yearlyMonthly - plan.yearlyPrice;
    return Math.round((savings / yearlyMonthly) * 100);
  };

  // Show simple loading while auth is being checked
  if (!initialAuthCheckDone) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-grey-50 via-white to-primary-25" />
    );
  }

  if (!user) return null;

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-grey-50 via-white to-primary-25 flex flex-col transition-opacity duration-300 ${
        isReady ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="max-w-5xl mx-auto px-4 py-4 sm:py-6 flex-1 flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={async () => {
              await signOut();
              navigate("/login");
            }}
            className="inline-flex items-center gap-2 text-grey-600 hover:text-grey-900 transition-colors text-sm font-medium"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            {t("pages.paywall.backToLogin")}
          </button>
        </div>

        <div className="text-center mb-4">
          <Link to="/">
            <img
              src={pebbleLogo}
              alt="Pebble"
              className="h-10 mx-auto mb-4 hover:opacity-80 transition-opacity"
            />
          </Link>
        </div>

        <div className="text-center mb-4">
          <h1 className="text-lg sm:text-xl font-semibold text-grey-900 mb-1">
            {t("pages.paywall.choosePlan")}
          </h1>
          <p className="text-grey-500 text-sm max-w-md mx-auto">
            {t("pages.paywall.planSubtitle")}
          </p>
        </div>

        {showCanceled && (
          <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg flex items-center gap-3">
            <XIcon className="w-5 h-5 text-primary-600" />
            <p className="text-primary-800">
              {t("pages.paywall.checkoutCanceled")}
            </p>
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <div className="flex flex-col items-center gap-1.5 mb-4">
          <div className="bg-grey-100 p-1 rounded-lg inline-flex">
            <button
              onClick={() => setBillingCycle("monthly")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                billingCycle === "monthly"
                  ? "bg-white text-grey-900 shadow-sm"
                  : "text-grey-600 hover:text-grey-900"
              }`}
            >
              {t("pages.paywall.monthly")}
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === "yearly"
                  ? "bg-white text-grey-900 shadow-sm"
                  : "text-grey-600 hover:text-grey-900"
              }`}
            >
              {t("pages.paywall.yearly")}
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-semibold">
                Save 17%
              </span>
            </button>
          </div>
          {billingCycle === "yearly" && (
            <p className="text-xs text-green-700 font-medium">
              ✓ Best for seasonal organizers — one payment, full year of access
            </p>
          )}
          {billingCycle === "monthly" && (
            <p className="text-xs text-grey-400">
              Switch to annual to save $489/year
            </p>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-4 flex-1">
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => plan.available && handleSelectPlan(plan.id)}
              className={`relative bg-white rounded-2xl border-2 transition-all ${
                plan.comingSoon
                  ? "border-grey-200 opacity-75 cursor-default"
                  : selectedPlan === plan.id
                  ? "border-primary-500 shadow-xl shadow-primary-100 ring-2 ring-primary-200 cursor-pointer"
                  : "border-grey-200 hover:border-grey-300 hover:shadow-lg cursor-pointer"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-primary-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              )}

              {plan.comingSoon && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-grey-500 text-white text-xs font-semibold px-4 py-1 rounded-full">
                    Coming Soon
                  </span>
                </div>
              )}

              <div className="p-5">
                <h3
                  className={`text-lg font-bold mb-1 ${
                    plan.comingSoon ? "text-grey-500" : "text-grey-900"
                  }`}
                >
                  {plan.name}
                </h3>
                <p
                  className={`text-sm mb-3 ${
                    plan.comingSoon ? "text-grey-400" : "text-grey-600"
                  }`}
                >
                  {plan.description}
                </p>

                <div className="mb-4">
                  {plan.comingSoon ? (
                    <div className="flex items-baseline gap-1">
                      <span className="text-lg font-medium text-grey-400">
                        Coming soon
                      </span>
                    </div>
                  ) : plan.freemium ? (
                    <>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg font-medium text-grey-400 line-through">$249/mo</span>
                        <span className="text-3xl font-bold text-grey-900">Free to start</span>
                      </div>
                      <p className="text-amber-600 text-xs mt-0.5 font-medium">15% platform fee on vendor payments</p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-2">
                        <span className="text-lg font-medium text-grey-400 line-through">$549/mo</span>
                        <span className="text-3xl font-bold text-grey-900">
                          ${billingCycle === "yearly" ? Math.round(plan.yearlyPrice / 12) : getPrice(plan)}
                        </span>
                        <span className="text-sm text-grey-500">/mo</span>
                      </div>
                      {billingCycle === "yearly" ? (
                        <p className="text-green-600 text-xs mt-0.5 font-medium">
                          Billed ${plan.yearlyPrice}/yr — save {getSavings(plan)}% vs monthly
                        </p>
                      ) : (
                        <p className="text-amber-600 text-xs mt-0.5">
                          Or ${plan.yearlyPrice}/yr — save {getSavings(plan)}%
                        </p>
                      )}
                      <p className="text-green-600 text-xs mt-1 font-medium">Unlock advanced tools & priority support</p>
                    </>
                  )}
                </div>

                <ul className="space-y-2 mb-4">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <CheckIcon
                        className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                          plan.comingSoon ? "text-grey-300" : "text-primary-500"
                        }`}
                      />
                      <span
                        className={`text-sm ${
                          plan.comingSoon ? "text-grey-400" : "text-grey-700"
                        }`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {plan.comingSoon ? (
                  <div className="space-y-3">
                    {notifyStatus === "success" ? (
                      <div className="flex items-center justify-center gap-2 py-2.5 px-4 bg-grey-900 rounded-lg">
                        <CheckCircleIcon className="w-5 h-5 text-white" />
                        <span className="text-sm font-medium text-white">
                          You're on the list!
                        </span>
                      </div>
                    ) : notifyStatus === "duplicate" ? (
                      <div className="flex items-center justify-center gap-2 py-2.5 px-4 bg-grey-800 rounded-lg">
                        <CheckCircleIcon className="w-5 h-5 text-grey-300" />
                        <span className="text-sm font-medium text-grey-300">
                          Already signed up
                        </span>
                      </div>
                    ) : showNotifyForm ? (
                      <form onSubmit={handleNotifySubmit} className="space-y-2">
                        <div className="relative">
                          <MailIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
                          <input
                            type="email"
                            value={notifyEmail}
                            onChange={(e) => setNotifyEmail(e.target.value)}
                            placeholder="Enter your email"
                            className="w-full pl-9 pr-3 py-2 text-sm border border-grey-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-grey-900 focus:border-transparent"
                            disabled={notifyStatus === "submitting"}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {notifyError && (
                          <p className="text-xs text-red-500">{notifyError}</p>
                        )}
                        <button
                          type="submit"
                          disabled={notifyStatus === "submitting"}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full py-2 px-4 bg-grey-900 text-white text-sm font-medium rounded-lg hover:bg-grey-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {notifyStatus === "submitting" ? (
                            <>
                              <svg
                                className="animate-spin h-4 w-4"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                  fill="none"
                                />
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                />
                              </svg>
                              Signing up...
                            </>
                          ) : (
                            "Notify Me"
                          )}
                        </button>
                      </form>
                    ) : (
                      <button
                        onClick={handleNotifyClick}
                        className="w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all bg-grey-900 text-white hover:bg-grey-800"
                      >
                        Notify Me
                      </button>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectPlan(plan.id);
                      handleSubscribe(plan.id);
                    }}
                    disabled={isProcessing}
                    className={`w-full py-2.5 px-4 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                      selectedPlan === plan.id
                        ? "bg-primary-600 text-white hover:bg-primary-700 shadow-md hover:shadow-lg active:scale-[0.98]"
                        : "bg-grey-100 text-grey-900 hover:bg-grey-200 shadow-md hover:shadow-lg active:scale-[0.98]"
                    } ${isProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isProcessing && selectedPlan === plan.id ? (
                      <>
                        <svg
                          className="animate-spin h-4 w-4"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCardIcon className="w-4 h-4" />
                        Subscribe Now
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="text-center py-3">
          <div className="inline-flex items-center gap-2 text-grey-500 text-xs mb-2">
            <LockClosedIcon className="w-3.5 h-3.5" />
            Your subscription is protected with secure Stripe payment
            processing.
          </div>

          <div className="flex justify-center gap-4 text-xs">
            <button
              onClick={() => setLegalModal({ isOpen: true, type: "terms" })}
              className="text-grey-500 hover:text-primary-600 underline"
            >
              Terms of Use
            </button>
            <button
              onClick={() => setLegalModal({ isOpen: true, type: "privacy" })}
              className="text-grey-500 hover:text-primary-600 underline"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>

      <LegalModal
        isOpen={legalModal.isOpen}
        onClose={() => setLegalModal({ isOpen: false, type: null })}
        type={legalModal.type}
      />
    </div>
  );
};

export default EOPaywall;
